/**
 * Dashboard API client for analytics and API key management.
 */

import { getAuthHeaders } from './authHeaders';

const API_BASE_URL = import.meta.env.VITE_API_ENDPOINT;
if (!API_BASE_URL) {
  throw new Error('Missing required environment variable: VITE_API_ENDPOINT');
}

export interface AnalyticsSummary {
  fromDate?: string;
  toDate?: string;
  totalImages: number;
  totalTransforms?: number;
  totalSizeBytes: number;
  totalSizeMB: number;
  apiKeyCount: number;
  avgPreprocessMs?: number;
  avgTransformMs?: number;
  preprocessErrors?: number;
  transformErrors?: number;
  facesDetected?: number;
  objectsDetected?: number;
}

export interface TransformationCounts {
  resize: number;
  crop: number;
  autofocus: number;
}

export interface PopularImage {
  mediaId: string;
  originalFilename: string;
  size: number;
  uploadDate: string;
  facesDetected: number;
  objectsDetected: number;
  transformCount?: number;
}

export interface CacheHitRatioPoint {
  ts: string;
  ratio: number;
  hitRequests: number;
  totalRequests: number;
  transformCount: number;
}

export interface AnalyticsImageTransformStat {
  mediaId: string;
  originalFilename: string;
  size: number;
  originalWidth?: number;
  originalHeight?: number;
  uploadDate: string;
  transformCount: number;
  firstTransformedAt?: number;
  lastTransformedAt?: number;
  minRequestedArea: number;
  maxRequestedArea: number;
  minRequestedSize?: string;
  maxRequestedSize?: string;
  divergenceArea: number;
}

export interface AnalyticsData {
  summary: AnalyticsSummary;
  transformations: TransformationCounts;
  dailyUploads: Record<string, number>;
  dailyTransforms?: Record<string, number>;
  popularImages: PopularImage[];
  topTransformed?: Array<{ mediaId: string; count: number }>;
  cacheHitRatio: number;
  cacheHitRatioSeries?: CacheHitRatioPoint[];
  maxTransformedImage?: AnalyticsImageTransformStat | null;
  mostDivergentImage?: AnalyticsImageTransformStat | null;
}

export interface AnalyticsEvent {
  PK: string;
  SK: string;
  event_type: string;
  mediaId?: string;
  clientId: string;
  timestamp: number;
  duration_ms?: number;
  metadata?: Record<string, unknown>;
}

export interface ListAnalyticsEventsResponse {
  items: AnalyticsEvent[];
  nextCursor: string | null;
}

export interface ListAnalyticsImagesResponse {
  items: AnalyticsImageTransformStat[];
  nextCursor: string | null;
  sortBy?: string;
  sortDir?: string;
}

export interface ImageAnalyticsData {
  mediaId: string;
  transformCount: number;
  avgTransformTime: number;
  firstTransformedAt: number;
  lastTransformedAt: number;
  minRequestedArea?: number;
  maxRequestedArea?: number;
  minRequestedSize?: string;
  maxRequestedSize?: string;
  histogram?: ImageHistogramData;
}

export interface ImageHistogramBin {
  bin: string;
  count: number;
}

export interface ImageHistogramData {
  mediaId: string;
  window: string;
  windowStart: string;
  windowEnd: string;
  asOf: number;
  bins: ImageHistogramBin[];
  totalTransforms: number;
}

export interface APIKey {
  keyId: string;
  name: string;
  maskedKey: string;
  createdAt: string;
  lastUsedAt: string | null;
  usageCount: number;
  rateLimit: number;
  status: string;
}

export interface NewAPIKey {
  apiKey: string;
  keyId: string;
  name: string;
  createdAt: string;
  rateLimit: number;
  status: string;
  message: string;
}

export interface ListAPIKeysResponse {
  apiKeys: APIKey[];
  count: number;
}

/**
 * Fetch analytics data for a user or public analytics if userId is 'global'.
 */
export async function getAnalytics(userId: string, fromDate?: string, toDate?: string): Promise<AnalyticsData> {
  const params = new URLSearchParams();
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  const query = params.toString();

  // Use public endpoint for global analytics (unauthenticated users)
  if (userId === 'global') {
    const response = await fetch(`${API_BASE_URL}/dashboard/public/analytics${query ? `?${query}` : ''}`);
    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch analytics' } }));
      throw new Error(error.error?.message || 'Failed to fetch analytics');
    }
    return response.json();
  }

  // Use authenticated endpoint for user-specific analytics
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/analytics${query ? `?${query}` : ''}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch analytics' } }));
    throw new Error(error.error?.message || 'Failed to fetch analytics');
  }

  return response.json();
}

/**
 * Fetch per-image analytics.
 */
export async function getImageAnalytics(userId: string, mediaId: string): Promise<ImageAnalyticsData> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/analytics/image/${mediaId}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch image analytics' } }));
    throw new Error(error.error?.message || 'Failed to fetch image analytics');
  }

  return response.json();
}

/**
 * Fetch materialized per-image histogram (last 30 days).
 */
export async function getImageHistogram(userId: string, mediaId: string): Promise<ImageHistogramData> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/analytics/image/${mediaId}/histogram`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to fetch image histogram' } }));
    throw new Error(error.error?.message || 'Failed to fetch image histogram');
  }

  return response.json();
}

/**
 * List raw analytics events (paginated).
 */
export async function listAnalyticsEvents(
  userId: string,
  limit = 50,
  cursor?: string
): Promise<ListAnalyticsEventsResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) {
    params.set('cursor', cursor);
  }
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/analytics/events?${params.toString()}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to list analytics events' } }));
    throw new Error(error.error?.message || 'Failed to list analytics events');
  }

  return response.json();
}

/**
 * List per-image transform analytics (paginated).
 */
export async function listAnalyticsImages(
  userId: string,
  limit = 25,
  cursor?: string,
  sortBy?: string,
  sortDir?: 'asc' | 'desc'
): Promise<ListAnalyticsImagesResponse> {
  const params = new URLSearchParams();
  params.set('limit', String(limit));
  if (cursor) {
    params.set('cursor', cursor);
  }
  if (sortBy) {
    params.set('sortBy', sortBy);
  }
  if (sortDir) {
    params.set('sortDir', sortDir);
  }
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/analytics/images?${params.toString()}`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to list analytics images' } }));
    throw new Error(error.error?.message || 'Failed to list analytics images');
  }

  return response.json();
}

/**
 * List API keys for a user.
 */
export async function listAPIKeys(userId: string): Promise<ListAPIKeysResponse> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/api-keys`, { headers });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to list API keys' } }));
    throw new Error(error.error?.message || 'Failed to list API keys');
  }

  return response.json();
}

/**
 * Create a new API key.
 */
export async function createAPIKey(userId: string, name: string): Promise<NewAPIKey> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/api-keys`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ name }),
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to create API key' } }));
    throw new Error(error.error?.message || 'Failed to create API key');
  }

  return response.json();
}

/**
 * Revoke an API key.
 */
export async function revokeAPIKey(
  userId: string,
  keyId: string
): Promise<{ keyId: string; status: string; message: string }> {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE_URL}/dashboard/${userId}/api-keys/${keyId}`, {
    method: 'DELETE',
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: { message: 'Failed to revoke API key' } }));
    throw new Error(error.error?.message || 'Failed to revoke API key');
  }

  return response.json();
}
